<template>
  <div>
    <loadable :loading="isBusy" size="small" />
    <div v-show="!isBusy">
      <div v-if="type === 'custom' && options.input">
        <component
          ref="input"
          :is="options.input"
          :v-model="value[field]"
          :required="required"
          :filter="filter"
          :options="options"
          :field="field"
          :model="value"
          :label="options.label"
          @input="value[field]=arguments[0]"
        />
      </div>
      <div v-if="!type || type === 'text' || type === 'number' || type === 'password' || type === 'data'">
        <v-text-field
          ref="input"
          :size="size"
          :type="type || 'text'"
          v-model="value[field]"
          @blur="blur"
          :placeholder="small ? options.label : null"
          :label="small ? null : options.label"
          :error-messages="options.errors"
          :rules="rules"
          :required="required"
          :hide-details="filter"
          :hint="options.hint"
          :persistent-hint="!!options.hint"
          :multi-line="options.multiLine"
        />
      </div>
      <div v-if="type === 'bool'">
        <v-checkbox ref="input" v-model="value[field]" @blur="blur" :label="small ? null : options.label" hide-details></v-checkbox>
      </div>
      <div v-if="type === 'enum'">
        <v-select
          ref="input"
          :size="size"
          v-model="value[field]"
          :items="enumValues"
          item-text="text"
          item-value="key"
          @blur="blur"
          :label="small ? null : options.label"
          :error-messages="options.errors"
          :rules="rules"
          :required="required"
          :hide-details="filter"
          autocomplete
        />
      </div>
      <div v-if="type === 'reference' && !isBusy">
        <object-select
          ref="input"
          :size="size"
          v-model="value[field]"
          :id-field="options.id || '_self'"
          :name-field="options.display || 'name'"
          :options="refOptions"
          :label="small ? null : options.label"
          :required="required"
          @blur="blur"
        />
      </div>
      <div v-if="type === 'references' && !isBusy">
        <div class="input-group input-group--dirty input-group--text-field multi-input" :class="{ 'required-error': multiEmpty && required }" ref="multiContainer">
          <label v-if="!small">{{ options.label }}{{ required ? '*' : '' }}</label>
          <div class="input-group__input">
            <multi-list-select
              ref="input"
              :selected-items="value[field]"
              :option-value="options.id || '_self'"
              :option-text="options.display || 'name'"
              :required="required"
              :list="refOptions"
              @blur="blur"
              @select="onSelect"
            />
          </div>
        </div>
      </div>
      <div v-if="type === 'weekdays' && !isBusy">
        <div class="input-group py-0">
          <label v-if="!small">{{ options.label }}</label>
        </div>
        <weekdays v-model="value[field]" />
      </div>
      <div v-if="type === 'date'">
        <date-range v-if="filter" v-model="value[field]" :label="options.label" />
        <date-time v-else v-model="value[field]" :label="options.label" />
      </div>
      <div v-if="type === 'datedtime'">
        <v-text-field
          :type="$browser.isIE() ? 'text' : 'time'"
          v-model="datedtime"
          :label="!small ? options.label : ''"
          placeholder="HH:MM"
          :error-messages="options.errors"
          :rules="rules"
          :required="required"
        />
      </div>
      <div v-if="type === 'color'">
        <div class="input-group py-0" v-if="!small">
          <label>{{ options.label }}</label>
        </div>
        <input type='color' v-model="value[field]" />
        <div class="pb-4" v-if="small" />
      </div>
    </div>
  </div>
</template>

<script>
import { MultiListSelect } from 'vue-search-select'
import ObjectSelect from '../ObjectSelect'
import Weekdays from '../Weekdays'
import moment from 'moment'
import DateTime from '../DateTime'
import DateRange from '../DateRange'

export default {
  props: {
    field: String,
    options: Object,
    value: Object,
    size: {
      type: String,
      default: ''
    },
    focus: {
      type: Boolean,
      default: false
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    overrideType: {
      type: String,
      default: null
    },
    overrideValues: {
      type: Array,
      default: null
    },
    noRules: Boolean,
    filter: Boolean
  },
  data () {
    let datedtime = null
    if (this.options.type === 'datedtime') {
      if (this.value[this.field]) {
        datedtime = moment(this.value[this.field]).format('HH:mm')
      }
    }

    if (this.options.type === 'enum' &&
      !this.filter &&
      this.value[this.field] !== undefined &&
      this.value[this.field] !== null &&
      typeof this.value[this.field] !== 'string') {
      this.value[this.field] = this.value[this.field].toString()
    }

    let date = null
    let time = null

    return {
      refOptions: [],
      isBusy: false,
      datedtime,
      date,
      time
    }
  },
  watch: {
    datedtime () {
      this.value[this.field] = moment(this.datedtime, 'hh:mm').toISOString()
    }
  },
  created () {
    if (this.type === 'reference' ||
      this.type === 'references'
    ) {
      if (this.overrideValues !== null) {
        this.refOptions = [].concat(this.overrideValues)
      } else if (this.options.model) {
        this.isBusy = true
        this.options.model
          .getList({
            filters: this.options.filters || {},
            sort: this.options.display || 'name'
          })
          .then(result => {
            this.refOptions = [].concat(result.items)
            this.isBusy = false

            if (this.focus) {
              this.doFocus()
            }

            if (this.type === 'reference' && this.allowEmpty) {
              this.addEmpty()
            }
          })
      } else if (typeof this.options.values === 'function') {
        this.isBusy = true
        Promise
          .resolve(this.options.values(this.value))
          .then(items => {
            this.refOptions = [].concat(items)
            this.isBusy = false

            if (this.focus) {
              this.doFocus()
            }

            if (this.type === 'reference' && this.allowEmpty) {
              this.addEmpty()
            }
          })
      } else {
        this.refOptions = [].concat(this.options.values)
        if (this.type === 'reference' && this.allowEmpty) {
          this.addEmpty()
        }
      }
    }
  },
  mounted () {
    if (this.focus) {
      this.doFocus()
    }
  },
  computed: {
    rules () {
      if (this.noRules) return []
      return (this.options.rules || []).map(rule => rule.bind(this.value))
    },
    small () {
      return this.size === 'sm'
    },
    type () {
      if (this.overrideType !== null) {
        return this.overrideType
      }
      return this.options && this.options.type
    },
    enumValues () {
      if (this.options.type !== 'enum') return null
      return Object.keys(this.options.values)
        .map(key => {
          return {
            key,
            text: this.options.values[key]
          }
        })
    },
    required () {
      if (this.filter) return false
      if (this.options.required !== undefined) {
        if (this.options.required === 'create') {
          return !this.value._self
        }
        return this.options.required
      }
    },
    multiEmpty () {
      if (this.type !== 'references') return false
      return !this.value[this.field] || this.value[this.field].length === 0
    }
  },
  methods: {
    save (e) {
      this.$emit('save', e, this.value, this.field)

      if (e) {
        e.stopPropagation()
        e.preventDefault()
      }
    },
    doFocus () {
      if (this.$refs.input) {
        let tested = (this.$refs.input.$el || this.$refs.input)
        if (typeof tested.focus === 'function') {
          tested.focus()
          setTimeout(() => {
            tested.focus()
          }, 100)
        }
      }
    },
    blur (e) {
      if (this.focus) {
        this.save()
      }
    },
    onSelect (items) {
      this.value[this.field] = items
    },
    addEmpty () {
      this.refOptions.unshift(null)
    },
    isValid () {
      if (this.options.type === 'custom') {
        return this.$refs.input.isValid()
      }

      if (this.options.type === 'references' && this.required) {
        if (this.multiEmpty) {
          return false
        }
      }

      if (this.options.rules.find(rule => rule.bind(this.value)(this.value[this.field]) !== true)) {
        return false
      }

      return true
    }
  },
  components: {
    MultiListSelect,
    ObjectSelect,
    Weekdays,
    DateRange,
    DateTime
  }
}
</script>

<style>
.multiple .menu {
  min-width: 400px !important;
}
.multiple .menu .item {
  font-size: 90% !important;
}

.ui.fluid.dropdown {
  padding: 0 0.5rem !important;
  font-size: 0.875rem !important;
  min-height: 1.1rem !important;
  border: 0 !important;
  border-radius: 0px !important;
  border-bottom: 1px solid rgba(0,0,0,.42) !important;
  background: none !important;
}
.ui.fluid.dropdown input {
  margin: 0 !important;
}
.ui.fluid.dropdown a.ui.label {
  border-radius: 0px !important;
  margin-top: 0.3em !important;
}

form.small,
form.small input {
  font-size: 100%;
}
form.small .input-group__details {
  min-height: auto !important;
}

.multi-input.required-error label {
  color: #ff0000 !important;
}

.multi-input.required-error .dropdown {
  border-color: #ff0000 !important;
}
</style>



// WEBPACK FOOTER //
// CrudInput.vue?b809f304